import { useState, useEffect, useMemo } from "react";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import ComponentLoader from "../pageLoader/ComponentLoader";

const Table = (props) => {
  const {
    tableData,
    columns,
    loading,
    pageName,
    userId,
    tableOptions = {},
  } = props;

  // ====== Keys for localStorage ======
  const columnStorageKey = useMemo(() => {
    const safePage = pageName?.toString().trim() || "defaultPage";
    const safeUser = userId?.toString().trim() || "anon";
    return `tableSettings:${safeUser}:${safePage}:columnVisibility`;
  }, [pageName, userId]);

  const sortStorageKey = useMemo(() => {
    const safePage = pageName?.toString().trim() || "defaultPage";
    const safeUser = userId?.toString().trim() || "anon";
    return `tableSettings:${safeUser}:${safePage}:sorting`;
  }, [pageName, userId]);

  // ====== Column Visibility State ======
  const [columnVisibility, setColumnVisibility] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const stored = localStorage.getItem(columnStorageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.warn("Failed to load column visibility", e);
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(columnStorageKey, JSON.stringify(columnVisibility));
    } catch (e) {
      console.warn("Failed to save column visibility", e);
    }
  }, [columnVisibility, columnStorageKey]);

  // ====== Sorting State ======
  const [sorting, setSorting] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(sortStorageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.warn("Failed to load sorting", e);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(sortStorageKey, JSON.stringify(sorting));
    } catch (e) {
      console.warn("Failed to save sorting", e);
    }
  }, [sorting, sortStorageKey]);

  // ====== Table Setup ======
  const table = useMantineReactTable({
    data: tableData,
    columns,
    enableFullScreenToggle: false,
    enableColumnResizing: true,
    autoResetPageIndex: false,
    initialState: {
      density: "xs",
    },
    state: {
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,

    mantineTableHeadProps: {
      sx: {
        backgroundColor: "#231c42",
        color: "white",
        textTransform: "uppercase",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        backgroundColor: "#231c42",
        "&, & *": {
          color: "white !important",
        },
      },
    },
    mantineTableBodyCellProps: {
      className: "text-[14px]",
    },
    ...tableOptions,
  });

  return (
    <div>
      {loading && <ComponentLoader />}
      <MantineReactTable table={table} />
    </div>
  );
};

export default Table;
